import { shallowEqual, useSelector, useStore } from 'react-redux'
import { dataFixStatus, masterClassification, titleName, viewId } from '../../../_constants/Code'
import { createBucketName } from '../../../common/utils/StringUtils'
import { useModal, useModalExtra } from '../../../_components/Modal'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { SecuritiesProviderStore, useSecuritiesReportDispatch, useSecuritiesReportSelector } from './extra/store'
import Forbidden from '../../../_components/Forbidden'
import LoadingMessage from '../../../_components/LoadingMessage'
import PageLoading from '../../../_components/PageLoading'
import RelatedDashboard from '../parts/RelatedDashboard'
import { CopyButton, SpinnerButton } from '../../common/CustomButton'
import { CircularProgress, Link } from '@mui/material'
import React from 'react'
import {
  fetchSecuritiesConfig,
  fetchSecuritiesMaster,
  fetchYearlyDataTask,
  updateSecuritiesConfig
} from './extra/services'
import { ChrofyCustomYearSelect } from '../../common/ChrofyCustomSelect'
import EmployeeCountCalculationMethod from './components/EmployeeCountCalculationMethod'
import GenderStatsCategorySetting from './components/GenderStatsCategorySetting'
import AggregationTargetSetting from './components/AggregationTargetSetting'
import { useModifyClassList as modifyClassList } from '../../../common/utils/CSSUtils'
import { extractYearFromFiscalYear, getCurrentFiscalYear } from './extra/helpers'

import _pick from 'lodash/pick'
import { runValidation } from './extra/validation'
import { errorLog } from '../../../common/logger/Logger'
import { getMessage } from '../../../common/utils/MessageUtils'
import {
  assertSecuritiesReportResponseSchema,
  assetSecuritiesReportStateSchema,
  convertRCompanyConfigToS,
  convertRGroupCompanyToS,
  GroupCompany,
  PageStateValueSchema
} from './extra/types'
import { z } from 'zod/mini'
import { produce } from 'immer'
import _get from 'lodash/get'

/**
 * @typedef {object} SecuritiesReportContentProps
 * @property {string} corporationNumber
 * @property {string} CorporationName
 * @property {string} accPeriod
 * @property {string} userId
 * @property {string} userName
 * @property {Record<string, unknown>} authority
 * @property {string} mode
 * @property {string} pageTitle
 * @property {string} bucketName
 * @property {ReturnType<useNavigate>} navigate
 * @property {ReturnType<useModal>} useModal
 * @property {ReturnType<useModalExtra>} modalExtra
 */
/** @type {React.FC<SecuritiesReportContentProps>} */
const SecuritiesReportInfoContent = (props) => {
  const store = useStore()
  const [loading, setLoading] = useState(true)

  const [isConfigLoading, setConfigLoading] = useState(false)

  const {
    selectedFiscalYear,
    yearList,
    master,
    errors: { server }
  } = useSecuritiesReportSelector((s) => _pick(s, ['yearList', 'selectedFiscalYear', 'master', 'errors']), shallowEqual)

  const yearListOptions = yearList.map((e) => ({ label: e.periodValue, value: e.fiscalYear }))
  const selectedYearListData = yearList.find((e) => e.fiscalYear === selectedFiscalYear)

  const dispatch = useSecuritiesReportDispatch()

  const _fetchSecuritiesConfig = (selectedYear) => (store) => fetchSecuritiesConfig(store, selectedYear)

  /** @function init */
  async function init() {
    document.title = props.pageTitle

    const [master, yearList, securitiesConfig] = await Promise.all(
      [
        fetchSecuritiesMaster,
        fetchYearlyDataTask,
        _fetchSecuritiesConfig(getCurrentFiscalYear(props.accPeriod, true))
      ].map((e) => e(store))
    )

    dispatch('INIT', {
      loading: false,
      master,
      yearList,
      securitiesConfig
    })
    setLoading(false)
  }

  useEffect(() => {
    init()
  }, [])

  const isCopyDisabled = yearListOptions.at(-1)?.value === selectedFiscalYear || isConfigLoading
  const isFixed = selectedYearListData?.dataFixStatus === dataFixStatus.fix.value

  /**
   * @function updateYear
   * @param {string} selectedFiscalYear - 選択された決算期の年度
   */
  async function updateYear(selectedFiscalYear) {
    try {
      const pageState = store.getState().pageState

      if (pageState[selectedFiscalYear]) {
        dispatch('CHANGE_FISCAL_YEAR', {
          selectedFiscalYear
        })
        return
      }

      setConfigLoading(true)

      const yearlyData = await _fetchSecuritiesConfig(selectedFiscalYear)(store, selectedFiscalYear)

      if (!yearlyData) {
        throw new Error('有価証券報告書レポート設定の取得に失敗しました')
      }

      dispatch('CHANGE_FISCAL_YEAR', {
        selectedFiscalYear,
        securitiesConfig: yearlyData
      })
    } catch (error) {
      console.log(error)
    } finally {
      setConfigLoading(false)
    }
  }

  /** @function copyPreviousYearSecuritiesConfig 前年度の設定をコピー */
  const copyPreviousYearSecuritiesConfig = async (previousYearSecurities, currentYearSecurities) => {
    const GroupCompanyList = z.array(GroupCompany)

    const [_currentYearSecurities, _previousYearSecurities] = await Promise.all([
      GroupCompanyList.parseAsync(currentYearSecurities),
      GroupCompanyList.parseAsync(previousYearSecurities)
    ])

    const previousYearCorpMap = new Map(_previousYearSecurities.map((e) => [e.subCorporationNumber, e]))

    return produce(_currentYearSecurities, (draft) => {
      draft.forEach((e) => {
        const subCorporationNumber = e.subCorporationNumber

        if (!previousYearCorpMap.has(subCorporationNumber)) {
          e.employeeCountInfo.aggregationTarget = e.employeeCountInfo.registrationStatus ? 'off' : 'none'
          e.employeeCountInfo.includeInSubmittingCompanyStatus = e.isChrofyEnabled
            ? e.employeeCountInfo.registrationStatus
              ? 'off'
              : 'none'
            : 'none'
          e.genderStatisticsInfo.femaleManagerRatio = e.genderStatisticsInfo.registrationStatus ? 'off' : 'none'
          e.genderStatisticsInfo.parentalLeaveRate = e.genderStatisticsInfo.registrationStatus ? 'off' : 'none'
          e.genderStatisticsInfo.wageGap = e.genderStatisticsInfo.registrationStatus ? 'off' : 'none'
        } else {
          const { employeeCountInfo, genderStatisticsInfo } = previousYearCorpMap.get(subCorporationNumber)

          e.employeeCountInfo.aggregationTarget = employeeCountInfo.aggregationTarget
          e.employeeCountInfo.includeInSubmittingCompanyStatus = employeeCountInfo.includeInSubmittingCompanyStatus
          e.genderStatisticsInfo.femaleManagerRatio = genderStatisticsInfo.femaleManagerRatio
          e.genderStatisticsInfo.parentalLeaveRate = genderStatisticsInfo.parentalLeaveRate
          e.genderStatisticsInfo.wageGap = genderStatisticsInfo.wageGap
        }
      })
    })
  }

  /** @function handlePreviousYearCopy */
  function handlePreviousYearCopy(store) {
    return async () => {
      const state = assetSecuritiesReportStateSchema(store.getState())
      const { selectedFiscalYear } = state
      const previousYear = extractYearFromFiscalYear(selectedFiscalYear) - 1
      const previousYearWithSuffix = `${previousYear}年度`

      const localPreviousYearSettings = state.pageState[previousYearWithSuffix]
      const currentYearGroupCompany = _get(state.pageState, [selectedFiscalYear, 'groupCompany'], [])

      let values = null

      if (localPreviousYearSettings) {
        const { companyConfig, groupCompany: previousGroupCompany } = localPreviousYearSettings
        const groupCompany = await copyPreviousYearSecuritiesConfig(previousGroupCompany, currentYearGroupCompany)

        values = {
          companyConfig,
          groupCompany
        }
      } else {
        const prevSettings = assertSecuritiesReportResponseSchema(
          await _fetchSecuritiesConfig(previousYearWithSuffix)(store)
        )

        const _prevCompanyConfig = convertRCompanyConfigToS(prevSettings)
        const _prevSecurities = convertRGroupCompanyToS(prevSettings)

        values = {
          companyConfig: _prevCompanyConfig,
          groupCompany: await copyPreviousYearSecuritiesConfig(_prevSecurities, currentYearGroupCompany)
        }
      }

      dispatch('UPDATE_STATE', {
        path: ['pageState', selectedFiscalYear],
        value: PageStateValueSchema.parse(values)
      })
    }
  }

  /** @function handleUpdateSecuritiesReport */
  async function handleUpdateSecuritiesReport() {
    updateServerErrors()
    try {
      await updateSecuritiesConfig(store)

      props.navigate('/master/masterList', { state: { message: '有報証券設定を保存しました。' } })
    } catch (error) {
      console.error(error)
      errorLog('E000001', viewId.securitiesReportInfo, 'handlePreviousYearCopy')
      updateServerErrors([getMessage('E011012')])
      scrollToServerError()
    }
  }

  const scrollToServerError = () => {
    document.getElementById('serverErrors')?.scrollIntoView({})
    const offset = 150
    window.scrollBy(0, -offset)
  }

  const updateClientErrors = (clientErrors) =>
    dispatch('UPDATE_STATE', {
      path: 'errors.client',
      value: clientErrors
    })

  const updateServerErrors = (value) =>
    dispatch('UPDATE_STATE', {
      path: 'errors.server',
      value: value ? value : []
    })

  /** @function handleOpenConfirmation 登録を確認子画面を表示 */
  function handleOpenConfirmation(store) {
    return async () => {
      const { promisify } = modifyClassList('.modal-window', ['w-[900px]'])

      updateServerErrors()
      updateClientErrors({})

      const { isError, clientErrors } = await runValidation(store)

      if (isError) {
        updateClientErrors(clientErrors)
        updateServerErrors(['入力内容に誤りがあります'])
        scrollToServerError()

        return
      }
      await promisify((res) => {
        props.showModal({
          title: 'マスター設定値登録の確認',
          onClose: res,
          isWide: false,
          description: (
            <>
              <div className="">設定値を登録してダッシュボードに反映します。よろしいですか？</div>
              <div className="mt-5 text-center">
                ※ダッシュボードへの反映には5分～10分程度かかります
                <br />
                マスター管理画面のステータスから反映状況を確認してください
              </div>
            </>
          ),
          button: [
            {
              name: '実行',
              className: 'footer-btn button_base',
              click: handleUpdateSecuritiesReport
            }
          ]
        })
      })
    }
  }

  if (loading) {
    return <PageLoading />
  }

  //レンダー
  return (
    <React.Fragment>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* 上階層に戻る */}
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                  </span>
                </Link>
              </div>
            </div>
            {/* コンテンツヘッダ */}
            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">
                {masterClassification[master.Classification].name}
              </h1>
            </header>
            <div className="page-body">
              <div id="serverErrors">
                {server?.map((e, index) => (
                  <p key={index} className="red--text">
                    {e}
                  </p>
                ))}
              </div>
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  有価証券報告書レポート設定
                </h2>
                <div className="my-6 ">
                  「10.6 有報開示指標（従業員の状況）」に表示する内容についての設定を行います。
                </div>

                <div className="flex justify-between mb-3">
                  <div className="flex items-center gap-2 ">
                    <label htmlFor="period">対象：</label>
                    <ChrofyCustomYearSelect
                      disabled={isConfigLoading}
                      data-testid={'yearSelect'}
                      onChange={updateYear}
                      hasNoSelect={false}
                      options={yearListOptions}
                      value={selectedFiscalYear}
                    />
                    {isConfigLoading && <CircularProgress size={16} style={{ color: 'black' }} />}
                  </div>
                  <div className="btn-group pl-10 border-l border-blue2-lighten-20">
                    <CopyButton onClick={handlePreviousYearCopy(store)} disabled={isCopyDisabled || isConfigLoading} />
                  </div>
                </div>

                {isFixed && <p className="red--text">対象決算期のデータが確定しているため設定変更できません。</p>}

                <div className="border-t border-blue1 mt-5" />

                {!isConfigLoading && (
                  <>
                    <Separator>
                      <EmployeeCountCalculationMethod />
                    </Separator>

                    <Separator>
                      <GenderStatsCategorySetting />
                    </Separator>

                    <Separator>
                      <AggregationTargetSetting />
                    </Separator>

                    <div className="content-footer mt-16 mb-8">
                      <div className="border-t border-blue1" />
                      {/*  ===== フッターボタン ===== */}
                      <div className="footer_button_area mt-8">
                        <Link
                          data-testid="cancelButton"
                          color="inherit"
                          variant="button"
                          underline="none"
                          component={RouterLink}
                          to="/master/masterList"
                        >
                          <button type="button" className="footer-btn button_back">
                            キャンセル
                          </button>
                        </Link>
                        {
                          <SpinnerButton
                            data-testid="submitButton"
                            name="登録"
                            disabled={isFixed}
                            className="footer-btn button_base"
                            onClick={handleOpenConfirmation(store)}
                          />
                        }
                      </div>
                    </div>
                  </>
                )}
                {isConfigLoading && <LoadingMessage message="データ読み込み中..." minHeightClassName="min-h-[200px]" />}
              </div>

              {master?.RelatedDashboard && (
                <RelatedDashboard
                  dashboards={[...master?.RelatedDashboard]}
                  authority={props.authority}
                  mode={props.mode}
                />
              )}
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  )
}

const Separator = ({ children }) => {
  return <div className="mt-5">{children}</div>
}

const SecuritiesReportInfo = () => {
  const showModal = useModal()
  const modalExtra = useModalExtra()
  const navigate = useNavigate()

  const state = useSelector(
    (state) => ({
      corporationNumber: state.company.corporationNumber,
      corporationName: state.company.name,
      accPeriod: state.company.accPeriod,
      userId: state.user.sub,
      userName: state.user.userName,
      authority: state.authority,
      mode: state.analysis.mode
    }),
    shallowEqual
  )

  // 権限を持っていない場合は画面表示しない
  if (
    !state.authority.Master_AuthInfo.Availability ||
    !state.authority.Master_AuthInfo.Auth_Conf.Dashboard.SecuritiesReport_Set
  ) {
    return <Forbidden />
  }

  const props = {
    ...state,
    pageTitle: titleName.securitiesReport + titleName.common,
    bucketName: createBucketName(process.env.REACT_APP_ENV, state.corporationNumber),
    navigate,
    showModal,
    modalExtra
  }

  return (
    <SecuritiesProviderStore>
      <SecuritiesReportInfoContent {...props} />
    </SecuritiesProviderStore>
  )
}

export default SecuritiesReportInfo
